// Story 2.3 — mutation hook for POST /api/v1/admin/drops.
//
// Class-based `AdminDropReleaseError` exposes the AppError's `code`
// discriminator + the relevant payload echoes so the composer page +
// detail-page release panel can branch toast copy + highlight specific
// fields. Generates `X-Idempotency-Key` per request so a network-retry
// replay returns the cached envelope from the backend's
// FINANCIAL_MUTATION_PREFIXES middleware (the prefix
// /api/v1/admin/drops was added in this story).

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../../lib/api-client";

export type ReleaseDropRequest = {
  portfolio_id: string;
  kind: "regular" | "special" | "welcome";
  price_cents: number;
  per_user_cap?: number | null;
  scheduled_at: string;
  retiring_at?: string | null;
  release_now?: boolean;
  linked_bulletin_post_id?: string | null;
};

export type ReleaseDropOutcome = {
  drop_id: string;
  portfolio_id: string;
  kind: string;
  status: "scheduled" | "live" | string;
  price_cents: number;
  per_user_cap: number | null;
  scheduled_at: string;
  live_at: string | null;
  retiring_at: string | null;
  linked_bulletin_post_id: string | null;
  created_at: string;
};

export type ReleaseDropResponse = {
  data: ReleaseDropOutcome;
};

export class AdminDropReleaseError extends Error {
  code: string;
  status: number;
  portfolioId?: string;
  bulletinPostId?: string;
  otherDropId?: string;
  recruitingStatus?: string;
  dropKind?: string;
  portfolioKind?: string;
  field?: string;
  reason?: string;
  provided?: number | null;
  kindEcho?: string;

  constructor(params: {
    message: string;
    code: string;
    status: number;
    portfolioId?: string;
    bulletinPostId?: string;
    otherDropId?: string;
    recruitingStatus?: string;
    dropKind?: string;
    portfolioKind?: string;
    field?: string;
    reason?: string;
    provided?: number | null;
    kindEcho?: string;
  }) {
    super(params.message);
    this.name = "AdminDropReleaseError";
    this.code = params.code;
    this.status = params.status;
    this.portfolioId = params.portfolioId;
    this.bulletinPostId = params.bulletinPostId;
    this.otherDropId = params.otherDropId;
    this.recruitingStatus = params.recruitingStatus;
    this.dropKind = params.dropKind;
    this.portfolioKind = params.portfolioKind;
    this.field = params.field;
    this.reason = params.reason;
    this.provided = params.provided;
    this.kindEcho = params.kindEcho;
  }
}

function generateIdempotencyKey(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof (crypto as Crypto).randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `release-drop-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

async function postReleaseDrop(
  request: ReleaseDropRequest,
): Promise<ReleaseDropResponse> {
  const response = await fetch("/api/v1/admin/drops", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Idempotency-Key": generateIdempotencyKey(),
    },
    body: JSON.stringify(request),
  });
  attachRequestIdToScope(response);

  if (!response.ok) {
    let code = "UNKNOWN";
    let message = `Release drop failed (HTTP ${response.status})`;
    let portfolioId: string | undefined;
    let bulletinPostId: string | undefined;
    let otherDropId: string | undefined;
    let recruitingStatus: string | undefined;
    let dropKind: string | undefined;
    let portfolioKind: string | undefined;
    let field: string | undefined;
    let reason: string | undefined;
    let provided: number | null | undefined;
    let kindEcho: string | undefined;
    try {
      const raw = await response.json();
      if (raw?.error?.code) code = raw.error.code;
      if (raw?.error?.message) message = raw.error.message;
      if (raw?.error?.portfolio_id) portfolioId = raw.error.portfolio_id;
      if (raw?.error?.bulletin_post_id) bulletinPostId = raw.error.bulletin_post_id;
      if (raw?.error?.other_drop_id) otherDropId = raw.error.other_drop_id;
      if (raw?.error?.recruiting_status)
        recruitingStatus = raw.error.recruiting_status;
      if (raw?.error?.drop_kind) dropKind = raw.error.drop_kind;
      if (raw?.error?.portfolio_kind) portfolioKind = raw.error.portfolio_kind;
      if (raw?.error?.field) field = raw.error.field;
      if (raw?.error?.reason) reason = raw.error.reason;
      if (raw?.error?.provided !== undefined) provided = raw.error.provided;
      if (raw?.error?.kind) kindEcho = raw.error.kind;
    } catch {
      // body not JSON
    }
    throw new AdminDropReleaseError({
      message,
      code,
      status: response.status,
      portfolioId,
      bulletinPostId,
      otherDropId,
      recruitingStatus,
      dropKind,
      portfolioKind,
      field,
      reason,
      provided,
      kindEcho,
    });
  }

  return (await response.json()) as ReleaseDropResponse;
}

export function useAdminCreateDrop() {
  const queryClient = useQueryClient();
  return useMutation<
    ReleaseDropResponse,
    AdminDropReleaseError,
    ReleaseDropRequest
  >({
    mutationFn: postReleaseDrop,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-portfolio", data.data.portfolio_id],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin-drop-for-portfolio", data.data.portfolio_id],
      });
    },
  });
}
